import { useEffect } from "react";

import type { BrandsListData } from "@similarweb/pro-api-client";
import { useQuery } from "@tanstack/react-query";

import { useDebounce } from "app/custom-hooks/useDebounce";

import { isDuplicateBrand } from "../../entry/utils/brandMultiSelectUtils";
import { fetchBrandAutocomplete } from "../api/fetchBrandAutocomplete";

const BRAND_AUTOCOMPLETE_DEBOUNCE_MS = 250;

export const useBrandAutocomplete = (
    query: string,
    excludedBrands: string[] = [],
): {
    suggestions: BrandsListData;
    isLoading: boolean;
    isError: boolean;
} => {
    const trimmedQuery = query.trim();
    const [debouncedQuery, setDebouncedQuery] = useDebounce("", BRAND_AUTOCOMPLETE_DEBOUNCE_MS);

    useEffect(() => {
        setDebouncedQuery(trimmedQuery);
    }, [setDebouncedQuery, trimmedQuery]);

    const { data, isFetching, isError } = useQuery({
        queryKey: ["autocomplete", "brands", debouncedQuery],
        queryFn: () => fetchBrandAutocomplete(debouncedQuery),
        enabled: debouncedQuery.length > 0,
        retry: false,
    });

    return {
        suggestions: (data ?? []).filter((item) => !isDuplicateBrand(item.name, [], excludedBrands)),
        isLoading: trimmedQuery.length > 0 && (isFetching || trimmedQuery !== debouncedQuery),
        isError,
    };
};
